import React from 'react';
import { format, parseISO } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import { CalendarDaysIcon, ClockIcon, ShieldCheckIcon, UsersIcon } from 'lucide-react';
import { BookingType, PriceEstimate, Property } from '../../types/booking';
import { fees } from '../../data/units';
import { formatNaira, pluralize } from '../../utils/format';
import { formatTime12 } from '../../utils/time';
import { AnimatedNaira } from '../AnimatedNumber';
interface PriceBreakdownProps {
  bookingType: BookingType;
  property: Property;
  checkIn: string | null;
  checkOut: string | null;
  shootDate: string | null;
  startTime: string | null;
  endTime: string | null;
  guests: number;
  estimate: PriceEstimate;
}
export function PriceBreakdown(props: PriceBreakdownProps) {
  const {
    bookingType,
    property,
    checkIn,
    checkOut,
    shootDate,
    startTime,
    endTime,
    guests,
    estimate
  } = props;
  const isStay = bookingType === 'apartment';
  const ready = isStay ? estimate.nights > 0 : true;
  return <aside className="overflow-hidden rounded-sm bg-ivory-50 shadow-lift ring-1 ring-forest-700/5 lg:sticky lg:top-28" data-id="element-140" data-mp="3bz9anj9e62s1">
      <div className="bg-forest-700 px-7 py-5" data-id="element-141" data-mp="3g2fgwqwi7tyi">
        <p className="text-[10px] uppercase tracking-wordmark text-gold-300" data-id="element-142" data-mp="3fz1e5hjxhiqj">Your booking</p>
        <AnimatePresence mode="wait" initial={false} data-id="element-143" data-mp="323xtkuaigjh4">
          <motion.p key={isStay ? property.id : 'shoot'} initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.18
        }} className="mt-1 font-display text-xl text-ivory-100" data-id="element-144" data-mp="371r23tzadpkk">
            {isStay ? property.name : 'Photoshoot session'}
          </motion.p>
        </AnimatePresence>
      </div>

      <div className="p-7" data-id="element-145" data-mp="3pxh0z8bxa5u8">
        <dl className="space-y-3 text-[13px]" data-id="element-146" data-mp="hk0brx98oh2w">
          <Row icon={<CalendarDaysIcon size={15} data-id="element-148" data-mp="hifjyfeb7c8q" />} label="Dates" data-id="element-147" data-mp="hi5ssohj2z1g">
            {isStay ? checkIn && checkOut ? `${format(parseISO(checkIn), 'EEE d MMM')} → ${format(parseISO(checkOut), 'EEE d MMM yyyy')} · ${pluralize(estimate.nights, 'night')}` : 'Choose your dates' : shootDate ? format(parseISO(shootDate), 'EEEE d MMMM yyyy') : 'Choose a date'}
          </Row>
          {!isStay && <Row icon={<ClockIcon size={15} data-id="element-150" data-mp="i64rp0pr4ueq" />} label="Time" data-id="element-149" data-mp="i5mpzokblnzf">
              {startTime && endTime ? `${formatTime12(startTime)} – ${formatTime12(endTime)}` : 'Choose a start time'}
            </Row>}
          <Row icon={<UsersIcon size={15} data-id="element-152" data-mp="8fjvtl1gq3zi" />} label="Guests" data-id="element-151" data-mp="8fa4nu4olqs8">
            {pluralize(guests, 'guest')}
          </Row>
        </dl>

        <div className="mt-6 border-t border-forest-700/10 pt-5" data-id="element-153" data-mp="3ffddpgzuz6n">
          {!ready ? <p className="text-[13px] leading-relaxed text-muted" data-id="element-154" data-mp="3um01p1fnzszz">Select your dates to see the price breakdown.</p> : <ul className="space-y-3" data-id="element-155" data-mp="3lnn0pbh9g7gq">
              <AnimatePresence initial={false} data-id="element-156" data-mp="3loprljel29wn">
                {estimate.lines.map(line => <motion.li key={line.label} layout initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} transition={{
              duration: 0.2,
              ease: [0.23, 1, 0.32, 1]
            }} className="flex items-start justify-between gap-4 overflow-hidden text-[13px]" data-id="element-157" data-mp="214kmqa9zsnpt">
                    <span className="text-muted" data-id="element-158" data-mp="1mg6grqha4j51">
                      {line.label}
                      {line.detail && <span className="block text-[12px] text-forest-300" data-id="element-159" data-mp="1rf10qvqzgdqq">{line.detail}</span>}
                    </span>
                    <span className={`shrink-0 text-right ${line.conditional ? 'text-[12px] text-gold-700' : 'text-ink'}`} data-id="element-160" data-mp="1w7awises480x">
                      {line.conditional ?? formatNaira(line.amount)}
                    </span>
                  </motion.li>)}
              </AnimatePresence>
            </ul>}
        </div>

        <div className="mt-6 flex items-baseline justify-between border-t border-forest-700/10 pt-5" data-id="element-161" data-mp="36aj3ytzvtt8n">
          <span className="text-[11px] uppercase tracking-overline text-gold-700" data-id="element-162" data-mp="31bfyx9jwjn0o">Estimated total</span>
          <AnimatedNaira value={ready ? estimate.total : 0} className="font-display text-3xl text-forest-700" data-id="element-163" data-mp="3f6s307jy6pu9" />
        </div>

        {isStay && <div className="mt-5 flex items-start gap-2.5 rounded-sm bg-gold-100 p-4 text-[12px] leading-relaxed text-forest-700" data-id="element-164" data-mp="3p224mu6fov7i">
            <ShieldCheckIcon size={15} className="mt-0.5 shrink-0 text-gold-600" aria-hidden="true" data-id="element-165" data-mp="3k3s9ukomfwmf" />
            <p data-id="element-166" data-mp="1xm7maaqolxn">
              <span className="font-normal" data-id="element-167" data-mp="1w2rcjp32avv">Refundable caution fee — {formatNaira(fees.cautionFee)}.</span> Mandatory and collected
              upfront to safeguard against property damage. It is refunded after checkout once the apartment is inspected.
            </p>
          </div>}

        <p className="mt-4 text-[11px] leading-relaxed text-muted" data-id="element-168" data-mp="1zqji4aeaqj69">
          This is an estimate. Final amounts are calculated and confirmed by our booking system.
        </p>
      </div>
    </aside>;
}
function Row({
  icon,
  label,
  children
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return <div className="flex items-start gap-2.5" data-id="element-169" data-mp="1kt9vyopp9lo9">
      <span className="mt-0.5 shrink-0 text-gold-500" aria-hidden="true" data-id="element-170" data-mp="1krys6ow98dx5">
        {icon}
      </span>
      <div data-id="element-171" data-mp="15uy18nlw0mzq">
        <dt className="sr-only" data-id="element-172" data-mp="15ydl03qiak7h">{label}</dt>
        <dd className="text-ink" data-id="element-173" data-mp="1avm364bnmo8q">{children}</dd>
      </div>
    </div>;
}